import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import { Lock, UserRound } from "lucide-react";

const FIELDS = [
  { key: "profile_visibility", label: "Profile visibility", options: ["members", "private"] },
  { key: "university_visibility", label: "University", options: ["visible", "hidden"] },
  { key: "research_topic_visibility", label: "Research topic", options: ["visible", "hidden"] },
  { key: "dissertation_title_visibility", label: "Dissertation title", options: ["visible", "hidden"] },
  { key: "graduation_year_visibility", label: "Expected graduation year", options: ["visible", "hidden"] },
  { key: "research_interests_visibility", label: "Research interests", options: ["visible", "hidden"] },
];

export default function Privacy() {
  const [prefs, setPrefs] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/privacy").then((r) => setPrefs(r.data)); }, []);
  const set = (k, v) => setPrefs((p) => ({ ...p, [k]: v }));
  const save = async () => {
    setSaving(true);
    try {
      await api.put("/privacy", prefs);
      toast.success("Privacy preferences saved.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (!prefs) return null;

  return (
    <div className="p-8 lg:p-12 max-w-2xl" data-testid="privacy-page">
      <p className="text-xs uppercase tracking-[0.25em] text-primary">Privacy</p>
      <h1 className="font-serif text-4xl lg:text-5xl font-black tracking-tight mt-3">Your visibility.</h1>
      <p className="text-muted-foreground mt-3">Your doctoral journey is private by default. Choose what other DocCircle members can see about you.</p>

      <Card className="mt-10 p-6 lg:p-8 space-y-5">
        {FIELDS.map((f) => (
          <div key={f.key} className="grid grid-cols-2 items-center gap-3">
            <Label className="text-sm">{f.label}</Label>
            <Select value={prefs[f.key]} onValueChange={(v) => set(f.key, v)}>
              <SelectTrigger data-testid={`priv-${f.key}`}><SelectValue /></SelectTrigger>
              <SelectContent>{f.options.map((o) => <SelectItem key={o} value={o}>{o.charAt(0).toUpperCase() + o.slice(1)}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        ))}
        <div className="pt-4 border-t border-border">
          <label className="flex items-start gap-3 cursor-pointer" data-testid="priv-peer-discovery-row">
            <Checkbox
              checked={!!prefs.appear_in_people_walking_path && prefs.profile_visibility !== "private"}
              onCheckedChange={(v) => set("appear_in_people_walking_path", !!v)}
              disabled={prefs.profile_visibility === "private"}
              data-testid="priv-appear-in-people"
              className="mt-0.5"
            />
            <div>
              <p className="text-sm flex items-center gap-2">
                <UserRound className="h-3.5 w-3.5 text-primary" />
                Appear in People Walking Your Path
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                When enabled, DocCircle may recommend your profile to doctoral candidates with similar academic paths. Never notified or ranked.
                {prefs.profile_visibility === "private" && <> Your profile is set to <span className="text-foreground font-medium">Private</span>, so you never appear regardless of this setting.</>}
              </p>
            </div>
          </label>
        </div>
        <div className="pt-4 border-t border-border">
          <p className="text-sm font-medium">Who can start a conversation with me?</p>
          <p className="text-xs text-muted-foreground mt-1">Blocking always overrides this. Existing accepted conversations remain readable.</p>
          <select
            value={prefs.who_can_message_me || "MATCHES_ONLY"}
            onChange={(e) => set("who_can_message_me", e.target.value)}
            data-testid="priv-who-can-message"
            className="mt-3 h-9 px-3 rounded-md border border-border bg-background text-sm w-full max-w-xs"
          >
            <option value="MATCHES_ONLY">Scholarly matches only</option>
            <option value="SHARED_CIRCLES">Members who share a Circle with me</option>
            <option value="DOCCIRCLE_MEMBERS">Any DocCircle member</option>
            <option value="NO_ONE">No one (accepted conversations still readable)</option>
          </select>
        </div>
        <div className="pt-4 border-t border-border flex items-start gap-3 text-xs text-muted-foreground">
          <Lock className="h-4 w-4 shrink-0 mt-0.5 text-primary" />
          <p>Doctoral journey progress is <span className="text-foreground font-medium">always private</span>. Only you can see your milestones and notes.</p>
        </div>
        <Button onClick={save} disabled={saving} className="rounded-full" data-testid="privacy-save-btn">
          {saving ? "Saving…" : "Save preferences"}
        </Button>
      </Card>
    </div>
  );
}
